import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Instagram, Youtube, Linkedin, Plus, Trash2, RefreshCw, Music2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

// Simple TikTok icon component
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

interface SocialAccount {
  _id: string;
  platform: 'instagram' | 'youtube' | 'linkedin' | 'tiktok';
  accountName: string;
  connectedAt: string;
  tokenExpiresAt?: string;
  metadata?: {
    channelId?: string;
    channelTitle?: string;
    profilePicture?: string;
  };
}

interface SocialAccountsPanelProps {
  onAccountsChange?: () => void;
}

export const SocialAccountsPanel: React.FC<SocialAccountsPanelProps> = ({
  onAccountsChange,
}) => {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<'instagram' | 'youtube' | 'linkedin' | 'tiktok' | null>(null);
  const { toast } = useToast();

  const fetchAccounts = async () => {
    try {
      const response = await fetch('/api/social/accounts', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Failed to fetch social accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleConnect = (platform: 'instagram' | 'youtube' | 'linkedin' | 'tiktok') => {
    setConnecting(platform);
    // Open OAuth in a popup window
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `/api/social/${platform}/connect`,
      `Connect ${platform}`,
      `width=${width},height=${height},left=${left},top=${top}`
    );

    // Poll for popup close
    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        setConnecting(null);
        fetchAccounts();
        onAccountsChange?.();
      }
    }, 500);
  };

  const handleDisconnect = async (accountId: string, platform: string) => {
    try {
      const response = await fetch(`/api/social/accounts/${accountId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast({
          title: 'Account disconnected',
          description: `Your ${platform} account has been disconnected.`,
        });
        fetchAccounts();
        onAccountsChange?.();
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect account. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const isTokenExpiring = (expiresAt?: string) => {
    if (!expiresAt) return false;
    const expiry = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry < 7;
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <Instagram className="h-5 w-5" />;
      case 'youtube':
        return <Youtube className="h-5 w-5" />;
      case 'linkedin':
        return <Linkedin className="h-5 w-5" />;
      case 'tiktok':
        return <TikTokIcon className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return 'bg-gradient-to-r from-purple-500 to-pink-500';
      case 'youtube':
        return 'bg-red-600';
      case 'linkedin':
        return 'bg-blue-600';
      case 'tiktok':
        return 'bg-black';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const connectedInstagram = accounts.find(a => a.platform === 'instagram');
  const connectedYouTube = accounts.find(a => a.platform === 'youtube');
  const connectedLinkedIn = accounts.find(a => a.platform === 'linkedin');
  const connectedTikTok = accounts.find(a => a.platform === 'tiktok');

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center justify-between">
          <span>Connected Accounts</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchAccounts}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Instagram */}
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
          <div className={`p-2 rounded-lg ${getPlatformColor('instagram')} text-white flex-shrink-0`}>
            <Instagram className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium">Instagram</p>
            {connectedInstagram ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground truncate">
                  @{connectedInstagram.accountName}
                </p>
                {isTokenExpiring(connectedInstagram.tokenExpiresAt) && (
                  <Badge variant="destructive" className="text-xs flex-shrink-0">
                    Token expiring
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Not connected</p>
            )}
          </div>
          {connectedInstagram ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDisconnect(connectedInstagram._id, 'Instagram')}
              className="text-destructive hover:text-destructive h-8 w-8 p-0"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleConnect('instagram')}
              disabled={connecting === 'instagram'}
              className="h-8 w-8 p-0"
            >
              {connecting === 'instagram' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {/* YouTube */}
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
          <div className={`p-2 rounded-lg ${getPlatformColor('youtube')} text-white flex-shrink-0`}>
            <Youtube className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium">YouTube</p>
            {connectedYouTube ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground truncate">
                  {connectedYouTube.metadata?.channelTitle || connectedYouTube.accountName}
                </p>
                {isTokenExpiring(connectedYouTube.tokenExpiresAt) && (
                  <Badge variant="destructive" className="text-xs flex-shrink-0">
                    Token expiring
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Not connected</p>
            )}
          </div>
          {connectedYouTube ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDisconnect(connectedYouTube._id, 'YouTube')}
              className="text-destructive hover:text-destructive h-8 w-8 p-0"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleConnect('youtube')}
              disabled={connecting === 'youtube'}
              className="h-8 w-8 p-0"
            >
              {connecting === 'youtube' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {/* LinkedIn */}
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
          <div className={`p-2 rounded-lg ${getPlatformColor('linkedin')} text-white flex-shrink-0`}>
            <Linkedin className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium">LinkedIn</p>
            {connectedLinkedIn ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground truncate">
                  {connectedLinkedIn.accountName}
                </p>
                {isTokenExpiring(connectedLinkedIn.tokenExpiresAt) && (
                  <Badge variant="destructive" className="text-xs flex-shrink-0">
                    Token expiring
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Not connected</p>
            )}
          </div>
          {connectedLinkedIn ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDisconnect(connectedLinkedIn._id, 'LinkedIn')}
              className="text-destructive hover:text-destructive h-8 w-8 p-0"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleConnect('linkedin')}
              disabled={connecting === 'linkedin'}
              className="h-8 w-8 p-0"
            >
              {connecting === 'linkedin' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {/* TikTok */}
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
          <div className={`p-2 rounded-lg ${getPlatformColor('tiktok')} text-white flex-shrink-0`}>
            <TikTokIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium">TikTok</p>
            {connectedTikTok ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground truncate">
                  @{connectedTikTok.accountName}
                </p>
                {isTokenExpiring(connectedTikTok.tokenExpiresAt) && (
                  <Badge variant="destructive" className="text-xs flex-shrink-0">
                    Token expiring
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Not connected</p>
            )}
          </div>
          {connectedTikTok ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDisconnect(connectedTikTok._id, 'TikTok')}
              className="text-destructive hover:text-destructive h-8 w-8 p-0"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleConnect('tiktok')}
              disabled={connecting === 'tiktok'}
              className="h-8 w-8 p-0"
            >
              {connecting === 'tiktok' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {accounts.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">
            Connect your social accounts to schedule posts
          </p>
        )}
      </CardContent>
    </Card>
  );
};
